import { useMemo, useCallback, useState, useEffect } from 'react'
import { Box } from '@mui/material'
import { allAvailableEventTypes } from 'src/consumer/constants'
import AssessmentReportBody from 'src/mentor/components/AssessmentReport/viewComponents/AssessmentReportBody'
import AssessmentReportHeader from 'src/mentor/components/AssessmentReport/viewComponents/AssessmentReportHeader'
import PrintableScreeningReportComponent from 'src/mentor/components/ScreeningReportMakingPage/printableReport/PrintableScreeningReportComponent'
import TherapyReportPreview from 'src/mentor/components/TherapyReportMakingPage/components/TherapyReportPreview'

export default function ReportPreviewComponent({ 
  auxiliaryData, 
  eventType,
  therapyReportRef,
  screeningReportRef,
  assessmentReportRef,
  getProcessAndSetEventData,
}) {
  const isTherapyReportEvent =
    eventType === allAvailableEventTypes?.therapySession ||
    eventType === allAvailableEventTypes?.nutritionConsultation
  // State to track latest report data for screening reports (from ref)
  const [latestTherapyReportData, setLatestTherapyReportData] = useState(null)
  const [latestTherapyReportEmrSections, setLatestTherapyReportEmrSections] = useState([])
  const [latestScreeningReportData, setLatestScreeningReportData] = useState(null)
  const [latestEmrSectionDataList, setLatestEmrSectionDataList] = useState([])
  const [latestEmrSectionPrintSettings, setLatestEmrSectionPrintSettings] = useState({})
  const [latestScreeningSections, setLatestScreeningSections] = useState(null)
  const [refreshKey, setRefreshKey] = useState(0)
  
  // Get latest data from screening report ref when it's available
  // Use a more efficient approach: only update when data actually changes
  useEffect(() => {
    if (eventType === allAvailableEventTypes.screening && screeningReportRef?.current) {
      let lastDataHash = ''
      
      // Try to get latest data from the ref
      const updateLatestData = () => {
        try {
          // Check if ref has a method to get latest data
          if (screeningReportRef.current?.getLatestReportData) {
            const latestData = screeningReportRef.current.getLatestReportData()
            
            // Create a hash of the data to detect changes (only compare key fields to avoid unnecessary updates)
            const dataHash = JSON.stringify({
              content: latestData.report?.content,
              observation: latestData.report?.observation,
              advice: latestData.report?.advice,
              screeningAnswersLength: latestData.report?.screeningAnswers?.length || 0,
              screeningAnswersIds: latestData.report?.screeningAnswers?.map(a => a?.questionId).sort().join(',') || '',
              screeningSectionsLength: latestData.screeningSections?.length || 0,
              screeningSectionsIds: latestData.screeningSections?.map(s => s?.id).sort().join(',') || '',
              emrSectionsLength: latestData.emrSectionDataList?.length || 0,
            })
            
            // Only update state if data actually changed (prevents unnecessary re-renders and blinking)
            if (dataHash !== lastDataHash) {
              console.log('[ReportPreviewComponent] Updating latest data:', {
                screeningAnswersCount: latestData.report?.screeningAnswers?.length || 0,
                screeningSectionsCount: latestData.screeningSections?.length || 0,
                screeningSections: latestData.screeningSections,
                screeningAnswers: latestData.report?.screeningAnswers,
              })
              setLatestScreeningReportData(latestData.report)
              setLatestEmrSectionDataList(latestData.emrSectionDataList || [])
              setLatestEmrSectionPrintSettings(latestData.emrSectionPrintSettings || {})
              setLatestScreeningSections(latestData.screeningSections)
              lastDataHash = dataHash
            }
          } else {
            // Fallback: use auxiliaryData but mark it as potentially stale
            if (!latestScreeningReportData) {
              setLatestScreeningReportData(auxiliaryData?.screeningReport)
              setLatestEmrSectionDataList([])
              setLatestScreeningSections(auxiliaryData?.screeningSections)
            }
          }
        } catch (error) {
          // Silently handle errors
        }
      }
      
      // Get data immediately
      updateLatestData()
      
      // Set up interval to periodically refresh (every 1500ms = 1.5 seconds) to reduce blinking
      // Only updates if data actually changed
      const interval = setInterval(updateLatestData, 1500)
      
      return () => clearInterval(interval)
    }
  }, [eventType, screeningReportRef, auxiliaryData])

  useEffect(() => {
    if (!isTherapyReportEvent || !therapyReportRef?.current) {
      return undefined
    }

    let lastDataHash = ''
    const updateLatestData = () => {
      try {
        if (!therapyReportRef.current?.getLatestReportData) {
          setLatestTherapyReportData((prev) => prev || auxiliaryData?.therapyReport)
          return
        }

        const latestData = therapyReportRef.current.getLatestReportData()
        const dataHash = JSON.stringify({
          reportId: latestData.report?.id,
          status: latestData.report?.status,
          content: latestData.report?.content,
          freeFormRichText: latestData.report?.freeFormRichText,
          observation: latestData.report?.observation,
          advice: latestData.report?.advice,
          nutritionSection: latestData.report?.nutritionSection,
          nextVisitDate: latestData.report?.nextVisitDate,
          nextVisitDateType: latestData.report?.nextVisitDateType,
          sectionsLength: latestData.therapyReportEmrSections?.length || 0,
        })

        if (dataHash !== lastDataHash) {
          setLatestTherapyReportData(latestData.report)
          setLatestTherapyReportEmrSections(latestData.therapyReportEmrSections || [])
          lastDataHash = dataHash
        }
      } catch (error) {
        // Keep the last known preview on transient ref/read failures.
      }
    }

    updateLatestData()
    const interval = setInterval(updateLatestData, 1500)
    return () => clearInterval(interval)
  }, [isTherapyReportEvent, therapyReportRef, auxiliaryData])
  
  const reportDataInfo = useMemo(() => {
    switch (eventType) {
      case allAvailableEventTypes.screening: {
        // Use latest data from ref if available, otherwise fall back to auxiliaryData
        const reportData = latestScreeningReportData || auxiliaryData?.screeningReport
        return {
          templateData: auxiliaryData?.screeningReportTemplate,
          reportData: reportData,
          emrSectionDataList: latestEmrSectionDataList,
          emrSectionPrintSettings: latestEmrSectionPrintSettings,
          screeningSections: latestScreeningSections || auxiliaryData?.screeningSections,
          ReportComponent: PrintableScreeningReportComponent,
        }
      }
      case allAvailableEventTypes?.therapySession:
      case allAvailableEventTypes?.nutritionConsultation:
        return {
          templateData: auxiliaryData?.therapyReportTemplate,
          reportData: latestTherapyReportData || auxiliaryData?.therapyReport,
          therapyReportEmrSections: latestTherapyReportEmrSections,
          ReportComponent: TherapyReportPreview,
        }
      case allAvailableEventTypes?.assessment: {
        const assessmentReport = auxiliaryData?.assessmentReport
        const AssessmentReportPreview = () => (
          <Box sx={{ background: '#FFF', padding: 2 }}>
            <AssessmentReportHeader
              reportData={assessmentReport}
              templateData={auxiliaryData?.therapyReportTemplate}
              therapyDate={auxiliaryData?.bookedEventInfo?.startTime || auxiliaryData?.bookedEventData?.startTime}
            />
            <AssessmentReportBody reportData={assessmentReport} />
          </Box>
        )
        return {
          reportData: assessmentReport,
          ReportComponent: AssessmentReportPreview,
        }
      }
      default:
        return null
    }
  }, [eventType, auxiliaryData, latestTherapyReportData, latestTherapyReportEmrSections, latestScreeningReportData, latestEmrSectionDataList, latestEmrSectionPrintSettings, latestScreeningSections, latestScreeningReportData?.screeningAnswers])
  
  // Create onIssueReport function for therapy reports
  // Use useCallback to ensure it always accesses the current ref value
  const onIssueReport = useCallback(async () => {
    if (!isTherapyReportEvent) {
      return
    }
    
    // Call saveTherapyReport with status=completed
    if (therapyReportRef?.current?.saveTherapyReport) {
      await therapyReportRef.current.saveTherapyReport({
        mode: 'issue',
        hideToast: false,
        refreshBookedEvent: true,
      })
    } else {
      throw new Error('saveTherapyReport function not available')
    }
  }, [isTherapyReportEvent, therapyReportRef])
  
  if (!reportDataInfo) {
    return null
  }
  
  // Only pass onIssueReport for therapy sessions when ref is available
  const props = {
    report: reportDataInfo?.reportData,
    hideHeadingText: true,
    isAlwaysPrinting: true,
    setReport: () => {},
    templateData: reportDataInfo?.templateData,
  }
  
  // Add EMR data and screening sections for screening reports
  if (eventType === allAvailableEventTypes.screening) {
    // Use latestScreeningSections directly if available, otherwise fall back to reportDataInfo
    // This ensures we always use the most up-to-date data
    const effectiveScreeningSections = latestScreeningSections || reportDataInfo?.screeningSections || auxiliaryData?.screeningSections
    
    props.emrSectionDataList = latestEmrSectionDataList.length > 0 ? latestEmrSectionDataList : reportDataInfo?.emrSectionDataList
    props.emrSectionPrintSettings = Object.keys(latestEmrSectionPrintSettings).length > 0 ? latestEmrSectionPrintSettings : reportDataInfo?.emrSectionPrintSettings
    props.screeningSections = effectiveScreeningSections
    props.bookedEventInfo = auxiliaryData?.bookedEventInfo || auxiliaryData?.bookedEventData
    console.log('[ReportPreviewComponent] Passing props to PrintableScreeningReportComponent:', {
      screeningSectionsCount: props.screeningSections?.length || 0,
      screeningAnswersCount: props.report?.screeningAnswers?.length || 0,
      screeningSections: props.screeningSections,
      screeningAnswers: props.report?.screeningAnswers,
      reportDataInfoScreeningSections: reportDataInfo?.screeningSections,
      latestScreeningSections: latestScreeningSections,
      effectiveScreeningSections: effectiveScreeningSections,
    })
  }
  
  // Only add onIssueReport if it's a therapy session and ref is available
  if (isTherapyReportEvent && therapyReportRef?.current) {
    props.onIssueReport = onIssueReport
    props.emrSections = reportDataInfo?.therapyReportEmrSections
    props.bookedEventInfo = auxiliaryData?.bookedEventInfo || auxiliaryData?.bookedEventData
  }
  
  return (
    <>
      <reportDataInfo.ReportComponent 
        {...props} 
      />
    </>
  )
}
